/**
 * The sit.
 *
 * Black in both themes, and dark by default rather than after a timeout — a bright
 * screen at minute twenty is a real failure, not a cosmetic one. One tap wakes the
 * display to a dim state for a few seconds and it goes back down on its own.
 *
 * There is no scrub bar. Seeking backwards into synthesised silence is meaningless
 * (that tone was generated on the fly and does not exist any more), and a person
 * meditating has no use for +15 seconds. Pause, resume, and end are the whole transport.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import type { Recording, SessionPlan } from '../types';

interface Props {
  recording: Recording;
  plan: SessionPlan;
  position: number;
  wallElapsed: number;
  playing: boolean;
  interruption: string | null;
  stalled: string | null;
  /** Whether the display should fade between interactions during a sit. */
  dimDuringSit: boolean;
  /** The back button was pressed; see state/back-guard.ts. */
  confirmingExit: boolean;
  onPause: () => void;
  onResume: () => void;
  onEnd: () => void;
  onKeepSitting: () => void;
}

const WAKE_MS = 6000;

function clock(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
}

export function Player({
  recording, plan, position, wallElapsed, playing, interruption, stalled, confirmingExit,
  dimDuringSit,
  onPause, onResume, onEnd, onKeepSitting,
}: Props) {
  const [awake, setAwake] = useState(true);
  const timer = useRef<number | null>(null);
  const [requestedExit, setRequestedExit] = useState(false);
  const playerContent = useRef<HTMLDivElement>(null);
  const restoreFocus = useRef<HTMLElement | null>(null);
  const wasExitOpen = useRef(false);
  const exitOpen = confirmingExit || requestedExit;
  if (exitOpen && !wasExitOpen.current) {
    restoreFocus.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  }
  wasExitOpen.current = exitOpen;

  const wake = () => {
    if (!dimDuringSit) return;
    setAwake(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setAwake(false), WAKE_MS) as unknown as number;
  };

  useEffect(() => {
    if (dimDuringSit) wake();
    else setAwake(true);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [dimDuringSit]);

  useEffect(() => {
    const content = playerContent.current;
    if (!content) return;
    content.inert = exitOpen;
    content.toggleAttribute('inert', exitOpen);
    if (!exitOpen && restoreFocus.current) {
      restoreFocus.current.focus();
      restoreFocus.current = null;
    }
    return () => {
      content.inert = false;
      content.removeAttribute('inert');
    };
  }, [exitOpen]);

  const remaining = Math.max(0, plan.actualSec - position);

  // Ending needs the same deliberateness as starting. A single tap to leave, against a
  // considered action to arrive, is the asymmetry that makes people leave by accident.
  const [endHold, setEndHold] = useState(0);
  const holdTimer = useRef<number | null>(null);
  const holdCompleted = useRef(false);
  const startEndHold = () => {
    if (holdTimer.current) return;
    holdCompleted.current = false;
    const began = Date.now();
    holdTimer.current = setInterval(() => {
      const p = Math.min(1, (Date.now() - began) / 900);
      setEndHold(p);
      if (p >= 1) {
        holdCompleted.current = true;
        cancelEndHold();
        onEnd();
      }
    }, 40) as unknown as number;
  };
  const cancelEndHold = () => {
    if (holdTimer.current) clearInterval(holdTimer.current);
    holdTimer.current = null;
    setEndHold(0);
  };
  useEffect(() => cancelEndHold, []);

  const requestExit = () => {
    if (holdCompleted.current) {
      holdCompleted.current = false;
      return;
    }
    setRequestedExit(true);
  };
  const keepSitting = () => {
    setRequestedExit(false);
    if (confirmingExit) onKeepSitting();
  };

  return (
    <div
      class="screen"
      data-testid="player-screen"
      onClick={wake}
      style={{
        background: 'var(--sit-ground)',
        color: awake ? 'var(--honeybrick)' : 'var(--sit-ink)',
        transition: 'color 900ms ease-out',
        justifyContent: 'center',
        cursor: 'default',
      }}
    >
      <div ref={playerContent} data-testid="player-content" class="shell stack"
           style={{ gap: 'var(--gap-4)', textAlign: 'center' }}>
        <div
          class="fig"
          style={{ fontSize: 'var(--step-5)', lineHeight: 1, fontWeight: 300 }}
        >
          {clock(remaining)}
        </div>

        <p class="fig" style={{ fontSize: 'var(--step--1)', margin: '0 auto' }}>
          {clock(wallElapsed)} actual sitting time
        </p>

        {interruption && (
          <p style={{ color: 'var(--warn)', fontSize: 'var(--step--1)', margin: '0 auto' }}
             role="alert" data-testid="audio-interruption">
            {interruption}
          </p>
        )}

        {/* A single hairline for the whole sit: how far through, and nothing else. */}
        <div style={{ height: '1px', background: 'currentColor', opacity: 0.25 }}>
          <div style={{
            height: '1px',
            width: `${Math.min(100, (position / plan.actualSec) * 100)}%`,
            background: 'currentColor',
            opacity: 1,
          }} />
        </div>

        <div
          style={{
            opacity: awake ? 1 : 0,
            transition: 'opacity 900ms ease-out',
            pointerEvents: awake ? 'auto' : 'none',
          }}
          class="stack"
          data-testid="awake-controls"
        >
          {/* What is showing here is the recording, not a readout of the sit. Telling
              someone how much silence is left is a way of ending the silence. */}
          <p style={{ fontSize: 'var(--step--1)', margin: '0 auto' }} class="dim">
            {recording.title} · {new Date(recording.recordedOn).getFullYear()}
          </p>

          {stalled && (
            <p style={{ color: 'var(--warn)', fontSize: 'var(--step--1)' }} role="alert">
              Audio stalled: {stalled}
            </p>
          )}

          <div class="row" style={{ justifyContent: 'center', gap: 'var(--gap-3)' }}>
            <button
              class="btn"
              style={{ borderColor: 'currentColor', color: 'inherit', minWidth: '7rem' }}
              onClick={(e) => { e.stopPropagation(); playing ? onPause() : onResume(); }}
            >
              {playing ? 'Pause' : 'Resume'}
            </button>
            <button
              class="btn btn-quiet"
              style={{
                color: 'inherit',
                opacity: 0.7,
                background: `linear-gradient(to right, currentColor ${endHold * 100}%, `
                  + `transparent ${endHold * 100}%)`,
              }}
              onPointerDown={(e) => { e.stopPropagation(); startEndHold(); }}
              onPointerUp={cancelEndHold}
              onPointerLeave={cancelEndHold}
              onPointerCancel={cancelEndHold}
              onKeyDown={(e) => {
                // Enter remains an ordinary button activation. Space may also be held,
                // but browser key repeat must never create parallel completion timers.
                if (e.key === ' ' && !e.repeat) startEndHold();
              }}
              onKeyUp={cancelEndHold}
              onClick={(e) => { e.stopPropagation(); requestExit(); }}
            >
              End sitting
            </button>
          </div>
        </div>
      </div>

      {exitOpen && <ExitQuestion onKeepSitting={keepSitting} onExit={onEnd} />}
    </div>
  );
}

/**
 * Asked when the back button is pressed during a sit.
 *
 * Staying is the default and has the focus, because the press this answers is the one
 * most likely to be an accident: a phone picked up in the dark, a palm on the edge of the
 * screen. The audio keeps playing while it is asked. Exiting ends the sit exactly as
 * "Hold to end" does and returns to the home screen, not the phone's.
 */
function ExitQuestion({ onKeepSitting, onExit }: { onKeepSitting: () => void; onExit: () => void }) {
  const stay = useRef<HTMLButtonElement>(null);
  const exit = useRef<HTMLButtonElement>(null);
  useEffect(() => { stay.current?.focus(); }, []);
  return (
    <div
      class="exit-question"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="exit-question-title"
      onClick={(e) => { e.stopPropagation(); if (e.target === e.currentTarget) onKeepSitting(); }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          onKeepSitting();
        } else if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === stay.current) {
            e.preventDefault();
            exit.current?.focus();
          } else if (!e.shiftKey && document.activeElement === exit.current) {
            e.preventDefault();
            stay.current?.focus();
          }
        }
      }}
    >
      <div class="exit-question-card stack">
        <p id="exit-question-title" class="serif" style={{ fontSize: 'var(--step-1)', margin: 0 }}>
          Are you sure you want to exit this sitting?
        </p>
        <div class="row" style={{ justifyContent: 'center', gap: 'var(--gap-3)' }}>
          <button ref={stay} class="btn" style={{ borderColor: 'currentColor', color: 'inherit' }}
                  onClick={onKeepSitting}>
            Keep sitting
          </button>
          <button ref={exit} class="btn btn-quiet" style={{ color: 'inherit', opacity: 0.8 }}
                  onClick={onExit}>
            Exit
          </button>
        </div>
      </div>
    </div>
  );
}
